<script lang="ts">
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { FolderPlus } from 'lucide-svelte';
  import { quotationsApi } from '$lib/api/quotations';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import { session } from '$lib/state/session.svelte';
  import { QUOTE_VERTICAL_META } from '$lib/types';
  import * as lifecycle from '$lib/utils/lifecycle';
  import SectionCard from './SectionCard.svelte';
  import QuoteTimeline from './QuoteTimeline.svelte';
  import QuoteActionsSummary from './QuoteActionsSummary.svelte';
  import ConversationPanel from './ConversationPanel.svelte';
  import TermsPanel from './TermsPanel.svelte';
  import QuoteProject from '$lib/components/projects/QuoteProject.svelte';

  const { quotationId }: { quotationId: number } = $props();

  const queryClient = useQueryClient();
  const quoteQuery = createQuery(() => ({
    queryKey: ['quotation', quotationId],
    queryFn: () => quotationsApi.get(quotationId)
  }));

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['quotation', quotationId] });
  }

  const headerMut = createMutation(() => ({
    mutationFn: (body: Record<string, unknown>) => quotationsApi.update(quotationId, body),
    onSuccess: invalidate,
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  const addSectionMut = createMutation(() => ({
    mutationFn: () =>
      quotationsApi.createSection(quotationId, { name: `Section ${(quoteQuery.data?.sections.length ?? 0) + 1}` }),
    onSuccess: invalidate,
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  function saveHeaderField(field: string, value: string) {
    headerMut.mutate({ [field]: value });
  }

  // Chip color per status — one of genba's five chip pairs (see .chip-*
  // in app.css). submitted=warn (awaiting pricing), priced=teal (being
  // actively reviewed), revised=danger (sent back for rework), matching
  // the same semantics PipelineBoard's column dots already use.
  const STATUS_CHIP: Record<string, string> = {
    draft: 'chip-gray',
    revised: 'chip-danger',
    submitted: 'chip-warn',
    priced: 'chip-teal',
    approved: 'chip-lime'
  };
  const STATUS_LABEL: Record<string, string> = {
    draft: 'Draft',
    revised: 'Revised',
    submitted: 'Submitted',
    priced: 'In review',
    approved: 'Approved'
  };
</script>

{#if quoteQuery.isLoading}
  <p class="tym-loading-state">Loading quotation&hellip;</p>
{:else if quoteQuery.isError}
  <p class="tym-error-state m-7">{getErrorMessage(quoteQuery.error)}</p>
{:else if quoteQuery.data}
  {@const quote = quoteQuery.data}
  {@const editable = lifecycle.canEditBOQ(quote, session.me?.user.id, session.isAdmin)}
  {@const pricingEditable = lifecycle.pricingEditableBy(quote, session.isAdmin)}
  <div class="pq-scroll flex-1 overflow-y-auto px-4 py-4 lg:px-7 lg:py-5">
    <div class="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-[22px] max-w-[1240px] mx-auto items-start">
      <!-- LEFT: BOQ -->
      <div class="flex flex-col gap-4 min-w-0">
        <div class="tym-card px-[22px] py-5">
          <div class="flex items-center gap-2.5 mb-1.5 flex-wrap">
            <span class="text-[10.5px] font-semibold tracking-[.04em] uppercase text-toyama-teal bg-toyama-teal/10 rounded-[5px] px-2 py-0.5">
              {QUOTE_VERTICAL_META[quote.vertical]?.label ?? quote.vertical}
            </span>
            <span class="font-mono text-xs text-dark-gray">{quote.formatted_number}</span>
            <span class="text-[10.5px] font-semibold tracking-[.04em] uppercase text-dark-gray">{quote.lot_label}</span>
            <span class="chip {STATUS_CHIP[quote.status] ?? 'chip-gray'}">
              {STATUS_LABEL[quote.status]}
            </span>
          </div>
          {#if editable}
            <input
              class="font-heading font-bold text-[22px] text-navy tracking-tight w-full bg-transparent border-none outline-none px-0 py-0.5"
              placeholder="Untitled project"
              value={quote.project_name}
              onchange={(e) => saveHeaderField('project_name', (e.currentTarget as HTMLInputElement).value)}
            />
            <div class="flex flex-wrap gap-x-2 gap-y-1 items-center text-[13.5px] text-dark-gray mt-1">
              <input
                class="bg-transparent border-none outline-none min-w-0"
                placeholder="Client name"
                value={quote.client_name}
                onchange={(e) => saveHeaderField('client_name', (e.currentTarget as HTMLInputElement).value)}
              />
              <span>&middot;</span>
              <input
                class="bg-transparent border-none outline-none min-w-0"
                placeholder="Location"
                value={quote.project_location}
                onchange={(e) => saveHeaderField('project_location', (e.currentTarget as HTMLInputElement).value)}
              />
              <span>&middot;</span>
              <span>{quote.currency}</span>
            </div>
          {:else}
            <!-- Once the quote can no longer be edited (e.g. approved), render
                 these as plain text rather than disabled inputs: a disabled
                 input's value isn't picked up by text search/copy/print or
                 accessibility tooling, which made a populated client/project
                 name look "blank" even though the data was there. -->
            <div class="font-heading font-bold text-[22px] text-navy tracking-tight px-0 py-0.5">
              {quote.project_name || 'Untitled project'}
            </div>
            <div class="flex flex-wrap gap-x-2 gap-y-1 items-center text-[13.5px] text-dark-gray mt-1">
              {#each [quote.client_name, quote.project_location, quote.currency].filter((s) => s && s.trim()) as segment, i (i)}
                {#if i > 0}<span>&middot;</span>{/if}
                <span>{segment}</span>
              {/each}
            </div>
          {/if}
        </div>

        {#each quote.sections as section (section.id)}
          <SectionCard {quotationId} {section} currency={quote.currency} canEditBOQ={editable} {pricingEditable} />
        {/each}

        {#if editable}
          <button
            type="button"
            class="flex items-center justify-center gap-2 bg-[var(--tym-white)] border-[1.5px] border-dashed border-toyama-cloudy-gray rounded-xl p-3.5 text-[13.5px] font-medium text-toyama-teal hover:border-toyama-teal"
            disabled={addSectionMut.isPending}
            onclick={() => addSectionMut.mutate()}
          >
            <FolderPlus class="h-[17px] w-[17px]" /> Add a section
          </button>
        {/if}

        <TermsPanel {quotationId} terms={quote.terms} vertical={quote.vertical} {editable} />
      </div>

      <!-- RIGHT RAIL -->
      <div class="flex flex-col gap-4">
        <QuoteProject {quote} canManage={session.isAdmin || quote.created_by_id === session.me?.user.id} />
        <QuoteTimeline status={quote.status} />
        <QuoteActionsSummary {quote} />
        <ConversationPanel quotationId={quote.id} />
      </div>
    </div>
  </div>
{/if}
